<template>
    <component :is="component" v-if="localized" v-bind="$props" />
    <v-radio
        v-else
        class="mb-4 d-flex radio-control custom-plan"
        active-class="active"
        on-icon="$checkCircle"
        color="white"
        :value="paymentPlan"
    >
        <template #label>
            <div class="radio-label align-content-center">
                <v-col class="provider-logo-container">
                    <PaymentPlanImage :provider-key="paymentPlan.providerKey" />
                </v-col>
                <div class="payment-plan-content-container">
                    <PaymentPlanContent
                        :payment-plan="paymentPlan"
                        :provider-key="paymentPlan.providerKey"
                        :fully-covered="fullyCovered"
                        :financed-amount-cents="financedAmountCents"
                    />
                </div>
                <div class="pp-monthly-payment-container">
                    <PaymentPlanCost
                        :payment-plan="paymentPlan"
                        :provider-key="paymentPlan.providerKey"
                        :grouped="grouped"
                        :suggested-payment="suggestedPayment"
                        :financed-amount-cents="financedAmountCents"
                    />
                </div>
            </div>
            <div class="plan-description">
                <PaymentPlanDescription :payment-plan="paymentPlan" :provider-key="paymentPlan.providerKey" />
            </div>
            <v-row v-if="downPaymentRequired" class="ma-4">
                <PaymentPlanDownPaymentTable
                    :formatted-treatment-total="formattedTreatmentTotal"
                    :formatted-credit-limit-requested="formattedCreditLimitRequested"
                    :formatted-remaining-patient-obligation="formattedRemainingPatientObligation"
                />
            </v-row>
        </template>
    </v-radio>
</template>

<script>
    import PaymentPlanImage from "@/components/paymentplan/PaymentPlanImage.vue"
    import PaymentPlanContent from "@/components/paymentplan/PaymentPlanContent.vue"
    import PaymentPlanCost from "@/components/paymentplan/PaymentPlanCost.vue"
    import PaymentPlanDescription from "@/components/paymentplan/PaymentPlanDescription.vue"
    import PaymentPlanDownPaymentTable from "@/components/paymentplan/PaymentPlanDownPaymentTable.vue"
    import LocalizedComponentMixin from "@/components/localization/LocalizedComponentMixin"
    import ApplyMode from "@/model/apply/ApplyMode"
    import { applicationState, providerState } from "@/state"
    import { formatCurrency } from "@/util/currency"

    export default {
        name: "PaymentPlanOption",
        components: {
            PaymentPlanCost,
            PaymentPlanContent,
            PaymentPlanImage,
            PaymentPlanDescription,
            PaymentPlanDownPaymentTable,
        },
        mixins: [LocalizedComponentMixin],
        props: {
            financedAmountCents: {
                type: Number,
            },

            grouped: {
                type: Boolean,
                default: false,
            },

            paymentPlan: {
                type: Object,
                required: true,
            },

            suggestedPayment: {
                type: Boolean,
            },

            fullyCovered: {
                type: Boolean,
                default: true,
            },
            providerKey: {
                type: String,
                required: false,
            },
        },
        data() {
            const treatmentTotal = applicationState.application.purchaseAmountCents
            const creditLimitRequested = this.getCreditLimitRequested()
            const remainingPatientObligation = this.getRemainingPatientObligation()
            return {
                componentType: "paymentPlanOption",
                applyMode: applicationState.applyMode,

                formattedTreatmentTotal: formatCurrency(treatmentTotal, "cents", true),
                formattedCreditLimitRequested: formatCurrency(creditLimitRequested, "cents", true),
                formattedRemainingPatientObligation: formatCurrency(remainingPatientObligation, "cents", true),
            }
        },
        computed: {
            downPaymentRequired() {
                return this.applyMode === ApplyMode.FULL
                    ? providerState.getApplyResponse(this.paymentPlan.providerKey)?.downPaymentRequired
                    : providerState.getPreAuthResponse(this.paymentPlan.providerKey)?.downPaymentRequired
            },
        },

        methods: {
            getCreditLimitRequested() {
                return Math.min(
                    200000,
                    Math.floor(applicationState.application.purchaseAmountCents)
                )
            },

            getRemainingPatientObligation() {
                return Math.max(0, applicationState.application.purchaseAmountCents - this.getCreditLimitRequested())
            },
        },
    }
</script>
